"use client"

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { validateAndFormatPostcode } from '@/lib/validation/postcode'

// Default postcode: 10 Downing Street, London
const DEFAULT_POSTCODE = 'SW1A 2AA'

interface PostcodeContextType {
  postcode: string
  updatePostcode: (newPostcode: string) => { isValid: boolean; formatted: string; error?: string }
  clearPostcode: () => void
}

const PostcodeContext = createContext<PostcodeContextType | undefined>(undefined)

export function PostcodeProvider({ children }: { children: ReactNode }) {
  const [postcode, setPostcode] = useState<string>(DEFAULT_POSTCODE)

  // Load saved postcode from localStorage on mount, or try geolocation, or use default
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedPostcode = localStorage.getItem('user_delivery_postcode')
      if (savedPostcode) {
        setPostcode(savedPostcode)
      } else {
        // Try to get postcode from geolocation
        tryGetPostcodeFromLocation().then(locationPostcode => {
          if (locationPostcode) {
            setPostcode(locationPostcode)
            // Optionally save it to localStorage
            localStorage.setItem('user_delivery_postcode', locationPostcode)
          }
          // If geolocation fails, we already have DEFAULT_POSTCODE set
        })
      }
    }
  }, [])

  // Helper function to get postcode from geolocation
  const tryGetPostcodeFromLocation = async (): Promise<string | null> => {
    try {
      // Check if geolocation is available
      if (!navigator.geolocation) {
        return null
      }

      // Get current position
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 5000,
          maximumAge: 300000, // Cache for 5 minutes
        })
      })

      const { latitude, longitude } = position.coords

      // Use a reverse geocoding API to get postcode
      // Using postcodes.io which is free for UK postcodes
      const response = await fetch(
        `https://api.postcodes.io/postcodes?lon=${longitude}&lat=${latitude}&limit=1`
      )

      if (response.ok) {
        const data = await response.json()
        if (data.result && data.result.length > 0) {
          return data.result[0].postcode
        }
      }

      return null
    } catch (error) {
      // Silently fail and use default
      console.log('Could not get postcode from location:', error)
      return null
    }
  }

  const updatePostcode = useCallback((newPostcode: string) => {
    const result = validateAndFormatPostcode(newPostcode)
    
    if (result.isValid) {
      setPostcode(result.formatted)
      if (typeof window !== 'undefined') {
        localStorage.setItem('user_delivery_postcode', result.formatted)
      }
    }
    
    return result
  }, [])

  const clearPostcode = useCallback(() => {
    setPostcode('')
    if (typeof window !== 'undefined') {
      localStorage.removeItem('user_delivery_postcode')
    }
  }, [])

  return (
    <PostcodeContext.Provider value={{ postcode, updatePostcode, clearPostcode }}>
      {children}
    </PostcodeContext.Provider>
  )
}

export function usePostcode() {
  const context = useContext(PostcodeContext)
  if (context === undefined) {
    throw new Error('usePostcode must be used within a PostcodeProvider')
  }
  return context
}
